import { MessageSquare, Clock, AlertTriangle, Flag, Zap, Calendar } from 'lucide-react';
import type { OversightStats as OversightStatsType } from '../../types';

interface OversightStatsProps {
  stats: OversightStatsType | null;
  loading: boolean;
}

export function OversightStats({ stats, loading }: OversightStatsProps) {
  const statCards = [
    {
      label: 'Total Conversations',
      value: stats?.total_conversations ?? 0,
      icon: MessageSquare,
      color: 'text-primary-600 dark:text-primary-400',
      bgColor: 'bg-primary-100 dark:bg-primary-900/40',
    },
    {
      label: 'Pending Reviews',
      value: stats?.pending_reviews ?? 0,
      icon: Clock,
      color: 'text-gray-600 dark:text-gray-300',
      bgColor: 'bg-gray-100 dark:bg-gray-700',
    },
    {
      label: 'Flagged',
      value: stats?.flagged_conversations ?? 0,
      icon: Flag,
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-100 dark:bg-amber-900/40',
    },
    {
      label: 'Active Alerts',
      value: stats?.active_alerts ?? 0,
      icon: AlertTriangle,
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/40',
    },
    {
      label: 'Avg Tokens/Chat',
      value: stats?.avg_tokens_per_conversation
        ? Math.round(stats.avg_tokens_per_conversation).toLocaleString()
        : 0,
      icon: Zap,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/40',
    },
    {
      label: 'This Week',
      value: stats?.conversations_this_week ?? 0,
      icon: Calendar,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/40',
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse dark:bg-gray-800 dark:border-gray-700"
          >
            <div className="h-8 w-8 bg-gray-200 rounded-lg mb-3 dark:bg-gray-700" />
            <div className="h-6 w-16 bg-gray-200 rounded mb-1 dark:bg-gray-700" />
            <div className="h-4 w-24 bg-gray-200 rounded dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {statCards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.label}
            className="bg-white rounded-xl border border-gray-200 p-4 transition-shadow duration-150 hover:shadow-sm dark:bg-gray-800 dark:border-gray-700"
          >
            <div className={`inline-flex p-2 rounded-lg ${card.bgColor} mb-3`}>
              <Icon size={20} className={card.color} />
            </div>
            <p className="text-2xl font-semibold text-gray-900 tabular-nums dark:text-gray-100">
              {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
          </div>
        );
      })}
    </div>
  );
}
